import { Box } from '@material-ui/core';
import classNames from 'classnames';
import { SGSForm, SGSFormDate } from 'components';
import { formHasField } from 'features/arretTravail/models/scenarii';
import { WorkStoppagePeriods } from 'features/arretTravail/models/StopInformation/workStoppage';
import moment from 'moment';
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import styles from '../../../../css/ArretTravail.module.scss';

type CotisationDateSectionProps = {
    store: any;
};

const RightsS3201Cotisation: React.FC<CotisationDateSectionProps> = ({ store }) => {
    const { watch, setValue } = useFormContext();
    const hasCotisationDate = formHasField(['workStoppage.rights.start', 'workStoppage.rights.end'], 'formAtFields', store);

    const watchLastDayWorked = watch('workStoppage.infos.lastDayWorked');
    const watchPeriod = watch('workStoppage.rights.period');

    useEffect(() => {
        if (!watchPeriod) {
            // Reset dates if no salary right is selected
            setValue('workStoppage.rights.start', '');
            setValue('workStoppage.rights.end', '');
            delete store?.workStoppage?.rights?.start;
            delete store?.workStoppage?.rights?.end;
            return;
        }

        const { start, end } = calculatePeriod(watchLastDayWorked, watchPeriod);
        if (start && end) {
            setValue('workStoppage.rights.start', start);
            setValue('workStoppage.rights.end', end);
        }
    }, [watchLastDayWorked, watchPeriod, setValue]);

    if (!hasCotisationDate) {
        delete store?.workStoppage?.rights?.start;
        delete store?.workStoppage?.rights?.end;
        return null;
    }

    return (
        <>
            <Box className={classNames(styles.title, 'mb-4')}>Période de cotisation :</Box>
            <SGSForm.Row>
                <SGSForm.Col md={6}>
                    <SGSFormDate inputVariant="outlined" name="workStoppage.rights.start" label="workStop.fields.workStoppage.rights.start" disabled />
                </SGSForm.Col>
                <SGSForm.Col md={6}>
                    <SGSFormDate inputVariant="outlined" name="workStoppage.rights.end" label="workStop.fields.workStoppage.rights.end" disabled />
                </SGSForm.Col>
            </SGSForm.Row>
        </>
    );
};

export default RightsS3201Cotisation;

/**
 * Calculates the start and end period based on the last day worked and the salary rights.
 *
 * @param {string} lastDayWorked - The last day worked in ISO 8601 date format.
 * @param {WorkStoppagePeriods} period - The salary rights category that determines the period.
 * @returns {{start: string, end: string}} An object containing the start and end date strings in 'MM/DD/YYYY' format.
 * If the last day worked or salary rights are invalid, returns an empty start and end.
 */
export const calculatePeriod = (lastDayWorked: string, period: WorkStoppagePeriods): { start: string; end: string } => {
    if (!isDateValid(lastDayWorked) || !isValidPeriod(period)) {
        return { start: '', end: '' }; // TODO :: throw error ??
    }

    const periods = {
        [WorkStoppagePeriods.Plus3Months]: () => calculateCivilMonthPeriod(lastDayWorked, 3),
        [WorkStoppagePeriods.NinetyDays]: () => calculateFixedDayPeriod(lastDayWorked, 90),
        [WorkStoppagePeriods.sixMonths]: () => calculateCivilMonthPeriod(lastDayWorked, 6),
        [WorkStoppagePeriods.twelveMonths]: () => calculateCivilMonthPeriod(lastDayWorked, 12),
        [WorkStoppagePeriods.OneYear]: () => calculateFixedDayPeriod(lastDayWorked, 365),
    };

    return periods[period]?.() ?? { start: '', end: '' };
};

/**
 * Validates whether the given date is a valid ISO 8601 date.
 *
 * @param {string} date - The date to validate.
 * @returns {boolean} True if the date is valid, otherwise false.
 */
const isDateValid = (date: string) => moment(date, moment.ISO_8601, true).isValid();

/**
 * Checks if the provided salary right is a valid member of the WorkStoppagePeriods enum.
 *
 * @param {WorkStoppagePeriods} period - The salary rights category to validate.
 * @returns {boolean} True if the salary right is valid, otherwise false.
 */
const isValidPeriod = (period: WorkStoppagePeriods) => Object.values(WorkStoppagePeriods).includes(period);

/**
 * Formats a Moment.js date object into 'MM/DD/YYYY' string format.
 *
 * @param {moment.Moment} date - The date to format.
 * @returns {string} The formatted date string.
 */
const formatDate = (date: moment.Moment) => date.format('MM/DD/YYYY');

/**
 * Calculates the start and end dates of a period based on a given date and the number of months.
 * The calculation adjusts the start date to the first day of the month that is the specified
 * number of months before the provided last worked date. The end date is the last day of the
 * month that concludes the period.
 *
 * Example:
 * If the last day worked is '2024-09-15' and the period is 6 months:
 * - The start date will be '2024-03-01'.
 * - The end date will be '2024-08-31'.
 *
 * @param {string} date - The last day worked, in ISO 8601 format (e.g., 'YYYY-MM-DD').
 * @param {number} months - The number of months to look back from the last worked day.
 * @returns {{start: string, end: string}} An object containing the calculated start and end dates of the period, formatted as 'YYYY-MM-DD'.
 */
const calculateCivilMonthPeriod = (date: string, months: number) => {
    const start = moment(date).subtract(months, 'months').startOf('month');
    const end = moment(start)
        .add(months - 1, 'months')
        .endOf('month');
    return { start: formatDate(start), end: formatDate(end) };
};

/**
 * Calculates the start and end dates of a period based on the number of days before a given date.
 * The period includes the provided last day worked, and the start date is calculated by subtracting
 * the specified number of days minus one, making the start date inclusive of the full period.
 *
 * Example:
 * If the last day worked is '2024-09-15' and the period is 90 days:
 * - The start date will be '2024-06-18'.
 * - The end date will be '2024-09-15'.
 *
 * @param {string} date - The last day worked, in ISO 8601 format (e.g., 'YYYY-MM-DD').
 * @param {number} days - The number of days to look back from the last worked day, inclusive of the last day.
 * @returns {{start: string, end: string}} An object containing the calculated start and end dates of the period, formatted as 'YYYY-MM-DD'.
 */
const calculateFixedDayPeriod = (date: string, days: number) => {
    const start = moment(date).subtract(days - 1, 'days');
    return { start: formatDate(start), end: formatDate(moment(date)) };
};
